/* eslint-disable max-lines */
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { BlueDotIcon } from '@components/shared/icons.tsx';
import { Duration } from 'luxon';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuButton,
  MenuItems,
  Portal,
  Transition
} from '@headlessui/react';
import classNames from 'classnames';
import cn from 'classnames';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import {
  CheckCircle2,
  CircleMinus,
  Edit,
  UserPlus,
  UserX,
  Minus
} from 'lucide-react';
import AddSpeaker from '@components/editors/transcript-editor/body/add-speaker.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useScrollbarMenuGuard } from '@utils/hooks/use-scrollbar-menu-guard.ts';

type RealtimeSpeakerProps = {
  speaker: string;
  offset_ms: number;
  index: number;
  setSpeaker: (value: string) => void;
};

export const RealtimeSpeaker: React.FC<RealtimeSpeakerProps> = ({
  speaker,
  offset_ms,
  index,
  setSpeaker
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(speaker ?? '');
  const [searchQuery, setSearchQuery] = useState('');

  const renameInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const scrollGuardRef = useScrollbarMenuGuard();
  const { speakers, updateSpeaker } = useRealtimeContext();

  // Options for the inline-rename <datalist> — the session's own speaker names.
  // Replaces the browser saved-form-data popover disabled via autoComplete=off.
  const renameSuggestions = useMemo(
    () => Array.from(new Set(speakers)),
    [speakers]
  );

  useEffect(() => {
    if (isEditingName && renameInputRef.current) {
      setTimeout(() => {
        if (renameInputRef.current) {
          renameInputRef.current.select();
        }
      }, 0);
    }
  }, [isEditingName]);

  const isNoName = (s: string) =>
    s.trim().toLowerCase() === 'speaker' || s.trim() === '';
  const parseNumericDefault = (s: string): number | null => {
    const t = s.trim();
    const m1 = t.match(/^speaker\s*(\d+)$/i);
    if (m1) return Number(m1[1]);
    const m2 = t.match(/^\d+$/);
    if (m2) return Number(t);
    return null;
  };

  const sortedSpeakers = useMemo(() => {
    const cmp = (a: string, b: string) => {
      const aIsChair = a.trim().toLowerCase() === 'chairperson';
      const bIsChair = b.trim().toLowerCase() === 'chairperson';
      if (aIsChair && !bIsChair) return -1;
      if (!aIsChair && bIsChair) return 1;

      const aNo = isNoName(a);
      const bNo = isNoName(b);
      if (aNo && !bNo) return 1;
      if (!aNo && bNo) return -1;
      if (aNo && bNo) return 0;

      const na = parseNumericDefault(a);
      const nb = parseNumericDefault(b);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      if (nb !== null) return 1;

      return a.localeCompare(b, undefined, {
        sensitivity: 'base',
        numeric: true
      });
    };
    return [...speakers].sort(cmp);
  }, [speakers]);

  const maxSpeakerLength = useMemo(() => {
    const allNames = [speaker, ...speakers];
    return Math.max(...allNames.map(s => (s || '').length));
  }, [speaker, speakers]);

  const filteredSpeakers = useMemo(() => {
    if (!searchQuery.trim()) return sortedSpeakers;
    const query = searchQuery.toLowerCase();
    return sortedSpeakers.filter(s => s.toLowerCase().includes(query));
  }, [sortedSpeakers, searchQuery]);

  const submitInlineRename = (close: () => void) => {
    const oldName = (speaker || '').trim();
    const next = (tempName || '').trim();
    if (!next) {
      setIsEditingName(false);
      setTimeout(close, 0);
      return;
    }
    if (oldName.toLowerCase() === next.toLowerCase()) {
      setIsEditingName(false);
      setTimeout(close, 0);
      return;
    }
    const existing = speakers.find(
      s => s.trim().toLowerCase() === next.toLowerCase()
    );
    const target = existing ?? next;
    updateSpeaker(index, target);
    setSpeaker(target);
    setIsEditingName(false);
    setTimeout(close, 0);
  };

  const actionItems = (close: () => void) => [
    {
      id: -1,
      icon: <UserPlus width={16} className="text-blue-500" />,
      text: t('add-speaker'),
      onClick: () => {
        setShowModal(true);
        close();
      }
    },
    {
      id: -2,
      icon: <UserX width={16} className="text-red-500" />,
      text: t('no-speaker'),
      onClick: () => {
        setSpeaker('');
        updateSpeaker(index, '');
        close();
      }
    },
    {
      id: -4,
      icon: <Minus width={16} className="text-gray-500" />,
      text: t('speaker') || 'Speaker',
      onClick: () => {
        setSpeaker('Speaker');
        updateSpeaker(index, 'Speaker');
        close();
      }
    }
  ];

  const speakerItems = filteredSpeakers.map((item, idx) => ({
    id: idx,
    icon:
      item === speaker ? (
        <CheckCircle2 width={16} />
      ) : (
        <CircleMinus width={16} />
      ),
    text: item,
    onClick: () => {
      setSpeaker(item);
      updateSpeaker(index, item);
    }
  }));

  return (
    <div
      className={cn('flex justify-between h-12 w-full')}
      contentEditable={false}
      onClick={e => {
        e.stopPropagation();
        if (window.getSelection) window.getSelection()?.removeAllRanges();
      }}
    >
      <div className="items-center flex align-middle text-[16px] font-semibold max-w-fit">
        <span
          className="inline-flex mr-2 items-center gap-x-1.5 rounded-full px-2 py-1 text-sm font-medium ring-1 ring-inset ring-blue-200 dark:ring-zinc-700"
          style={{
            maxWidth: `min(${Math.min(maxSpeakerLength + 10, 100)}ch, 85vw)`
          }}
          title={speaker}
        >
          <BlueDotIcon className="h-1.5 w-1.5 fill-blue-500 dark:fill-blue-400/60" />

          <Menu as="div" className="relative">
            {({ close }) => (
              <>
                <MenuButton
                  className="flex text-start gap-x-2"
                  aria-label={`${t('speaker')}: ${speaker || 'Speaker'}. ${t('change-speaker') || 'Change speaker'}`}
                >
                  <input
                    className={cn(
                      'min-w-[6rem] w-auto outline-none border-none min-h-5 cursor-pointer'
                    )}
                    style={{
                      maxWidth: `min(${Math.min(maxSpeakerLength + 2, 100)}ch, 90vw)`
                    }}
                    value={speaker || 'Speaker'}
                    title={speaker}
                    aria-label={t('speaker') || 'Speaker'}
                    tabIndex={-1}
                    readOnly
                  />
                </MenuButton>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Portal>
                    <MenuItems
                      anchor="bottom start"
                      style={{
                        width: `min(${Math.min(maxSpeakerLength + 10, 120)}ch, 95vw)`,
                        minWidth: '20rem',
                        backgroundColor: getColour('thirdLayer'),
                        borderColor: getColour('border'),
                        color: getColour('normalText')
                      }}
                      className={cn(
                        'z-[999] mt-2.5 origin-top-left',
                        'rounded-xl border backdrop-blur',
                        'shadow-lg p-1'
                      )}
                    >
                      <div
                        className="sticky top-0 z-10 p-2 border-b"
                        style={{
                          backgroundColor: getColour('thirdLayer'),
                          borderColor: getColour('hairline')
                        }}
                      >
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder={
                            t('search-speakers') || 'Search speakers...'
                          }
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Escape') {
                              setSearchQuery('');
                              searchInputRef.current?.blur();
                            }
                          }}
                          style={{
                            backgroundColor: getColour('secondLayer'),
                            borderColor: getColour('border'),
                            color: getColour('normalText')
                          }}
                          className="w-full rounded-2xl border px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                        />
                      </div>

                      <div
                        ref={scrollGuardRef}
                        className="overflow-y-auto overscroll-contain"
                        style={{ maxHeight: '280px' }}
                      >
                        {speakerItems.length > 0
                          ? speakerItems.map(item => {
                              const isActive = item.text === speaker;
                              return (
                                <button
                                  key={`spk-${item.text}`}
                                  type="button"
                                  onClick={item.onClick}
                                  className={classNames(
                                    'flex w-full items-center gap-1 rounded-2xl px-3 py-1 text-left text-sm leading-5 transition hover:bg-primary/5'
                                  )}
                                >
                                  <span className="shrink-0">
                                    {isActive ? (
                                      <CheckCircle2 width={16} />
                                    ) : (
                                      <CircleMinus width={16} />
                                    )}
                                  </span>
                                  <span
                                    className={cn(
                                      'break-words flex-1',
                                      item.text.trim().toLowerCase() ===
                                        'chairperson' && 'font-bold'
                                    )}
                                  >
                                    {item.text}
                                  </span>
                                  {isActive && (
                                    <span className="ml-auto opacity-80 shrink-0">
                                      <CheckCircle2 width={14} />
                                    </span>
                                  )}
                                </button>
                              );
                            })
                          : searchQuery && (
                              <div
                                className="px-3 py-4 text-center text-sm"
                                style={{ color: getColour('inactiveText') }}
                              >
                                {t('no-speakers-found') || 'No speakers found'}
                              </div>
                            )}

                        <div
                          className="my-1 h-px"
                          style={{ backgroundColor: getColour('hairline') }}
                        />

                        <div
                          className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-left text-sm leading-5 hover:bg-primary/5"
                          onClick={e => e.stopPropagation()}
                          onKeyDownCapture={e => e.stopPropagation()}
                          tabIndex={-1}
                        >
                          {!isEditingName ? (
                            <button
                              type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => {
                                setTempName(speaker ?? '');
                                setIsEditingName(true);
                              }}
                              className="flex items-center gap-2 w-full"
                            >
                              <Edit
                                width={16}
                                className="shrink-0"
                                style={{ color: getColour('inactiveText') }}
                              />
                              <span className="break-words flex-1 text-start">
                                {t('rename-this-speaker')}
                              </span>
                            </button>
                          ) : (
                            <form
                              className="flex items-center gap-2 w-full"
                              onSubmit={e => {
                                e.preventDefault();
                                submitInlineRename(close);
                              }}
                              onKeyDownCapture={e => e.stopPropagation()}
                            >
                              <input
                                ref={renameInputRef}
                                autoFocus
                                type="text"
                                name="realtime-speaker-name"
                                list={`realtime-speaker-suggestions-${index}`}
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                                value={tempName}
                                onChange={e => setTempName(e.target.value)}
                                onBlur={() => submitInlineRename(close)}
                                onKeyDown={e => {
                                  e.stopPropagation();
                                  if (e.key === 'Escape') {
                                    setIsEditingName(false);
                                    setTimeout(close, 0);
                                  }
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submitInlineRename(close);
                                  }
                                }}
                                style={{
                                  backgroundColor: getColour('secondLayer'),
                                  borderColor: getColour('border'),
                                  color: getColour('normalText')
                                }}
                                className="w-full rounded-2xl border px-2 py-1 text-sm outline-none"
                                placeholder={t('enter-new-name')}
                              />
                              <datalist
                                id={`realtime-speaker-suggestions-${index}`}
                              >
                                {renameSuggestions.map(name => (
                                  <option key={name} value={name} />
                                ))}
                              </datalist>
                              <button
                                type="submit"
                                style={{
                                  borderColor: getColour('border'),
                                  color: getColour('normalText')
                                }}
                                className="rounded-2xl px-2 py-1 text-xs font-medium border hover:bg-primary/5"
                              >
                                {t('save')}
                              </button>
                            </form>
                          )}
                        </div>

                        <div
                          className="my-1 h-px"
                          style={{ backgroundColor: getColour('hairline') }}
                        />

                        {actionItems(close).map(item => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={item.onClick}
                            className={classNames(
                              'flex w-full items-center gap-1 rounded-2xl px-3 py-1 text-left text-sm leading-5 transition hover:bg-primary/5'
                            )}
                          >
                            <span className="shrink-0">{item.icon}</span>
                            <span className="break-words flex-1">
                              {item.text}
                            </span>
                          </button>
                        ))}
                      </div>
                    </MenuItems>
                  </Portal>
                </Transition>
              </>
            )}
          </Menu>
        </span>

        <span>{`${Duration.fromMillis(offset_ms || 0).toFormat('hh:mm:ss')}`}</span>
      </div>

      <div className="flex w-full justify-end py-1 h-12">
        {/* Editing tools can be added here later */}
      </div>

      <AddSpeaker
        show={showModal}
        onClose={() => setShowModal(false)}
        setSpeaker={(value: string) => {
          setSpeaker(value);
          updateSpeaker(index, value);
        }}
      />
    </div>
  );
};
